import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorHealth5, ColorNordicGrey4 } from 'autogen/design-tokens/tokens';
import type { BasketMessageSampleGroupViewModel } from 'autogen/swagger/all';
import { AddSample } from 'autogen/translation-keys/trans-website-basket-samples-singlesample';
import { Here } from 'autogen/translation-keys/trans-website-shared';
import InlineButton from 'components/generic/InlineButton';
import { Typography } from 'components/generic/typography';
import WithIcon from 'components/hoc/withIcon/WithIcon';
import Icon from 'components/Icon';
import useSingleSampleSelect from 'components/samples/components/SingleSampleSelection/shared/hooks/useSingleSampleSelect';
import { openSamplesModal } from 'components/samples/components/samplesPicker';
import useChooseSample from 'components/samples/components/samplesPicker/hooks/useChooseSample';
import format from 'helpers/translations';
import { formatReact } from 'modules/helpers/strings';
import BasketMessageContainer from '../shared/styles';

const StyledBasketMessageContainer = styled(BasketMessageContainer)<{ $achievement?: boolean }>`
    background-color: ${({ $achievement }) => ($achievement ? ColorHealth5 : ColorNordicGrey4)};
`;

export default function SampleMessage({
    model: { text, intro, hasOnlyOneSample }
}: {
    model: BasketMessageSampleGroupViewModel;
}) {
    const ref = useRef<HTMLDivElement>(null);
    // The samplePicker button is a part of the translation as html which is we have to do this currently.
    // The translation should render a react component instead like the formattedText below.
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        const button = ref?.current?.querySelector('.js-samplesOpenModal');

        if (button) {
            button.addEventListener('click', () => {
                openSamplesModal(button.innerHTML);
            });

            return () => {
                button.removeEventListener('click', () => {
                    openSamplesModal(button.innerHTML);
                });
            };
        }
    }, []);

    const { t } = useTranslation();
    const handleSingleSampleSelect = useSingleSampleSelect();
    const handleOpenChooseSample = useChooseSample();

    const formattedText = hasOnlyOneSample
        ? formatReact(text)(<InlineButton onClick={handleSingleSampleSelect}>{t(AddSample)}</InlineButton>)
        : formatReact(text)(<InlineButton onClick={handleOpenChooseSample}>{t(Here)}</InlineButton>);

    if (intro) {
        return (
            <StyledBasketMessageContainer $achievement>
                <WithIcon icon={<Icon type="Checkmark" size={16} />}>
                    <Typography.Body as="span" size="S">
                        {format(intro)} {formattedText}
                    </Typography.Body>
                </WithIcon>
            </StyledBasketMessageContainer>
        );
    }

    return (
        <StyledBasketMessageContainer ref={ref}>
            <Typography.Body as="span" size="S">
                {format(text)}
            </Typography.Body>
        </StyledBasketMessageContainer>
    );
}
